import { React, BrainIcon, CodeIcon, SmartphoneIcon, ShieldIcon, CpuIcon, DatabaseIcon, LightbulbIcon, SearchIcon, ChevronDownIcon, CheckIcon, XIcon } from '../vendor/runtime.js';

export const PROJECT_THEMES = [
  { value: 'AI/ML', label: 'AI / Machine Learning', description: 'Intelligent models, predictions & automation', Icon: BrainIcon, tone: 'purple' },
  { value: 'Web Development', label: 'Web Development', description: 'Websites, dashboards & full-stack platforms', Icon: CodeIcon, tone: 'blue' },
  { value: 'Mobile App', label: 'Mobile Application', description: 'Android, iOS & cross-platform experiences', Icon: SmartphoneIcon, tone: 'cyan' },
  { value: 'Blockchain', label: 'Blockchain', description: 'Smart contracts & decentralised applications', Icon: ShieldIcon, tone: 'orange' },
  { value: 'IoT', label: 'Internet of Things', description: 'Connected devices, sensors & smart systems', Icon: CpuIcon, tone: 'green' },
  { value: 'Data Science', label: 'Data Science', description: 'Data analysis, visualisation & insights', Icon: DatabaseIcon, tone: 'pink' },
  { value: 'Other', label: 'Something different', description: 'Have another idea? Tell us about it.', Icon: LightbulbIcon, tone: 'slate' },
];

export function ProjectThemeSelect({ value, onChange, disabled, invalid, describedBy, id = 'cp-theme' }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const trigger = React.useRef(null);
  const popup = React.useRef(null);
  const search = React.useRef(null);
  const uid = React.useId();
  const selected = PROJECT_THEMES.find(theme => theme.value === value);
  const options = PROJECT_THEMES.filter(theme => `${theme.label} ${theme.description}`.toLowerCase().includes(query.trim().toLowerCase()));
  const CurrentIcon = selected?.Icon || LightbulbIcon;
  function close(restore = true) {
    popup.current?.hidePopover?.();
    setOpen(false);
    if (restore) trigger.current?.focus();
  }
  function show() {
    setQuery('');
    setActive(Math.max(0, PROJECT_THEMES.findIndex(theme => theme.value === value)));
    setOpen(true);
  }
  function choose(theme) {
    if (!theme || disabled) return;
    onChange({ target: { name: 'theme', value: theme.value } });
    close();
  }
  React.useLayoutEffect(() => {
    if (!open) return;
    const node = popup.current;
    const position = () => {
      const rect = trigger.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 330), window.innerWidth - 24);
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const upward = below < 300 && above > below;
      Object.assign(node.style, {
        width: `${width}px`, left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
        top: upward ? 'auto' : `${rect.bottom + 8}px`, bottom: upward ? `${window.innerHeight - rect.top + 8}px` : 'auto',
        maxHeight: `${Math.max(140, Math.min(490, upward ? above : below))}px`,
      });
    };
    position();
    node.showPopover?.();
    search.current?.focus();
    const outside = event => { if (!node.contains(event.target) && !trigger.current.contains(event.target)) close(false); };
    const scroll = event => { if (!node.contains(event.target)) position(); };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [open]);
  React.useEffect(() => {
    if (open) document.getElementById(`${uid}-option-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open, query]);
  return <div className="pt-select">
    <button ref={trigger} id={id} type="button" disabled={disabled} className={`pt-trigger ${selected ? 'has-value' : ''}`} aria-label={`Project theme: ${selected?.label || 'Choose your project theme'}`} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? `${uid}-list` : undefined} aria-invalid={invalid || undefined} aria-describedby={describedBy}
      onClick={() => open ? close() : show()} onKeyDown={event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); show(); } }}>
      <span className={`pt-icon pt-${selected?.tone || 'purple'}`}><CurrentIcon aria-hidden="true"/></span>
      <span className="pt-trigger-copy"><strong>{selected?.label || 'Choose your project theme'}</strong><small>{selected?.description || 'Find the right category for your idea'}</small></span>
      <span className="pt-chevron"><ChevronDownIcon aria-hidden="true"/></span>
    </button>
    {open && <div ref={popup} popover="manual" className="pt-popup" onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      else if (event.key === 'Tab') { close(); }
      else if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); setActive(index => options.length ? (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length : 0); }
      else if (event.key === 'Enter') { event.preventDefault(); choose(options[active]); }
    }}>
      <header className="pt-popup-header"><div><strong>Explore project themes</strong><span>Choose the best fit for your project</span></div><button type="button" onClick={() => close()} aria-label="Close theme menu"><XIcon/></button></header>
      <div className="pt-search"><SearchIcon aria-hidden="true"/><input ref={search} type="search" role="combobox" aria-label="Search project themes" aria-expanded="true" aria-controls={`${uid}-list`} aria-autocomplete="list" aria-activedescendant={options[active] ? `${uid}-option-${active}` : undefined} value={query} onChange={event => { setQuery(event.target.value); setActive(0); }} placeholder="Search themes…"/></div>
      <div className="pt-options" id={`${uid}-list`} role="listbox" aria-label="Project themes">
        {options.map((theme, index) => <div key={theme.value} id={`${uid}-option-${index}`} role="option" aria-label={theme.label} aria-selected={theme.value === value} className={`pt-option ${index === active ? 'is-active' : ''}`} onPointerMove={() => setActive(index)} onMouseDown={event => event.preventDefault()} onClick={() => choose(theme)}>
          <span className={`pt-icon pt-${theme.tone}`}><theme.Icon aria-hidden="true"/></span><span className="pt-option-copy"><strong>{theme.label}</strong><small>{theme.description}</small></span><span className="pt-check">{theme.value === value && <CheckIcon aria-hidden="true"/>}</span>
        </div>)}
        {!options.length && <p className="pt-no-results" role="status">No matching themes. Try another search.</p>}
      </div><footer className="pt-popup-footer">Not sure? Choose “Something different” and describe your idea.</footer>
    </div>}
  </div>;
}
